<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { messageStamp } from 'shared/helpers/timeHelper';
import { ref, watch } from 'vue';
import { BaseTableRow, BaseTableCell } from 'dashboard/components-next/table';

const props = defineProps({
  automation: {
    type: Object,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['edit', 'clone', 'delete', 'toggle', 'export']);

const automationEnabled = ref(false);

const { toLocale } = useAutomationLocales();

const readableDate = date =>
  messageStamp(new Date(date).getTime() / 1000, 'LLL d, yyyy');
const readableDateWithTime = date =>
  messageStamp(new Date(date).getTime() / 1000, 'LLL d, yyyy hh:mm a');

const onAutomationEnabledChange = value => {
  automationEnabled.value = !value;
  emit('toggle');
};

watch(
  () => props.automation.active,
  newVal => {
    automationEnabled.value = newVal;
  },
  { immediate: true }
);
</script>

<template>
  <BaseTableRow :item="automation">
    <template #default>
      <BaseTableCell class="max-w-0 w-full">
        <div class="flex flex-col items-start gap-1">
          <span class="block text-heading-3 text-n-slate-12 capitalize">
            {{ automation.name }}
          </span>
          <p class="mb-0 text-n-slate-11 text-body-main line-clamp-2">
            {{ automation.description }}
          </p>
        </div>
      </BaseTableCell>

      <BaseTableCell>
        <span class="text-body-main text-n-slate-11 whitespace-nowrap">
          {{
            toLocale(automation.trigger?.label, automation.trigger?.type || '-')
          }}
        </span>
      </BaseTableCell>

      <BaseTableCell>
        <ToggleSwitch
          v-model="automationEnabled"
          @change="onAutomationEnabledChange"
        />
      </BaseTableCell>

      <BaseTableCell :title="readableDateWithTime(automation.updated_at)">
        <span class="text-body-main text-n-slate-12 whitespace-nowrap">
          {{ readableDate(automation.updated_at) }}
        </span>
      </BaseTableCell>

      <BaseTableCell align="end">
        <div class="flex gap-3 justify-end flex-shrink-0">
          <Button
            v-tooltip.top="$t('WGPT_AUTOMATIONS.LIST.TOOLTIP.EDIT')"
            icon="i-woot-edit-pen"
            sm
            slate
            :is-loading="loading"
            @click="$emit('edit')"
          />
          <Button
            v-tooltip.top="$t('WGPT_AUTOMATIONS.LIST.TOOLTIP.CLONE')"
            icon="i-woot-clone"
            slate
            sm
            :is-loading="loading"
            @click="$emit('clone')"
          />
          <Button
            v-tooltip.top="$t('WGPT_AUTOMATIONS.LIST.TOOLTIP.EXPORT')"
            icon="i-lucide-upload"
            slate
            sm
            :is-loading="loading"
            @click="$emit('export')"
          />
          <Button
            v-tooltip.top="$t('WGPT_AUTOMATIONS.LIST.TOOLTIP.DELETE')"
            icon="i-woot-bin"
            slate
            sm
            class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
            :is-loading="loading"
            @click="$emit('delete')"
          />
        </div>
      </BaseTableCell>
    </template>
  </BaseTableRow>
</template>
